import * as React from 'react';
import { chakra, type HTMLChakraProps } from '@chakra-ui/react';

/**
 * Project Pressable.
 *
 * Replaces NativeBase's `Pressable`. Chakra v3 has no direct equivalent —
 * the chosen pattern is a semantic `<button>` with the native chrome
 * reset (`appearance: none`, transparent bg, no border) so consumers
 * control all visuals via Chakra style props.
 *
 * NB `onPress` maps to standard `onClick`. NB render-prop children
 * (`{({ isHovered, isPressed }) => ...}`) are NOT supported — express
 * those states with Chakra's `_hover` / `_active` style props instead.
 *
 * Defaults: `display: inline-flex`, `flexDirection: column`,
 * `alignItems: stretch`, center-aligned text (matches native `<button>`
 * / NB selection-card usage), no bg / border. Width is content-sized by
 * default (`inline-flex`) so pill/chip rows wrap horizontally — pass
 * `w="100%"` explicitly when wrapping a full-width row, and
 * `textAlign="left"` when you need start-aligned content.
 */
export type PressableProps = Omit<HTMLChakraProps<'button'>, 'onClick'> & {
  href?: string;
  target?: string;
  rel?: string;
  /** NB-compatible alias for `onClick`. */
  onPress?: HTMLChakraProps<'button'>['onClick'];
  onClick?: HTMLChakraProps<'button'>['onClick'];
};

const sharedStyles = {
  appearance: 'none' as const,
  bg: 'transparent',
  borderWidth: 0,
  cursor: 'pointer',
  // inline-flex keeps the control content-sized (NB Pressable parity)
  // so chip/pill rows flow horizontally. `display: flex` would make
  // each Pressable a full-width block and stack them vertically.
  display: 'inline-flex',
  flexDirection: 'column' as const,
  alignItems: 'stretch' as const,
  textAlign: 'center' as const,
  _disabled: { cursor: 'not-allowed' },
};

export const Pressable = React.forwardRef<HTMLButtonElement, PressableProps>(
  ({ href, target, rel, onPress, onClick, ...props }, ref) => {
    const handleClick = onClick ?? onPress;
    if (href) {
      return (
        <chakra.button
          asChild
          {...sharedStyles}
          {...props}
          onClick={handleClick}
        >
          <a
            href={href}
            target={target}
            rel={rel ?? (target === '_blank' ? 'noreferrer' : undefined)}
          >
            {props.children}
          </a>
        </chakra.button>
      );
    }
    return (
      <chakra.button
        ref={ref}
        type="button"
        {...sharedStyles}
        {...props}
        onClick={handleClick}
      />
    );
  }
);
Pressable.displayName = 'Pressable';
